<template>
  <b-modal id="order-lens-modal" size="xl" hide-footer="" @show="onShow" @hide="onHide"
    ><template v-slot:modal-header="{ close }">
      <b-container>
        <div class="modal-title">
          <h3 class="text-bold text-center">Order Lenses</h3>
          <h6 class="text-uppercase text-center">{{ modalTitle }}</h6>
          <button class="close" type="button" aria-label="Close" @click="close">
            <i class="di-remove-10"></i>
          </button>
        </div>
      </b-container>
    </template>
    <div class="container" v-if="orderLensData">
      <ValidationObserver v-slot="{ handleSubmit }">
        <b-form
          class="mb-5"
          ref="orderLensForm"
          @submit.stop.prevent="handleSubmit(sendOrder)"
          novalidate="novalidate"
        >
          <div class="repeat-reason-box" v-if="orderLensData.count > 0">
            <div class="title">
              <h6>Repeat Order</h6>
              <span>This will be a repeat order. Please choose a reason for the order</span>
            </div>
            <b-row class="small-gutter">
              <b-col sm="4">
                <ValidationProvider name="reason" rules="required" v-slot="validationContext">
                  <b-form-group label="Reason" label-for="reason">
                    <b-form-select
                      id="delivery"
                      name="reason"
                      v-model="form.reason"
                      :state="getValidationState(validationContext)"
                      :options="reasonOptions"
                      aria-describedby="to-feedback"
                      ><template v-slot:first="">
                        <b-form-select-option :value="null" disabled=""
                          >Please select</b-form-select-option
                        >
                      </template></b-form-select
                    >
                    <b-form-invalid-feedback id="to-feedback">{{
                      validationContext.errors[0]
                    }}</b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              <b-col sm="8">
                <b-form-group label="Internal Note" label-for="description">
                  <ValidationProvider
                    name="description"
                    rules="required"
                    v-slot="validationContext"
                  >
                    <b-form-textarea
                      id="description"
                      name="description"
                      v-model="form.description"
                      rows="2"
                      :state="getValidationState(validationContext)"
                      placeholder="i.e. received HC, not HMC+"
                      aria-describedby="description-feedback"
                    ></b-form-textarea>
                    <b-form-invalid-feedback id="description-feedback">{{
                      validationContext.errors[0]
                    }}</b-form-invalid-feedback>
                  </ValidationProvider>
                </b-form-group>
              </b-col>
            </b-row>
          </div>
          <b-row class="mb-3">
            <b-col class="d-flex align-items-center" sm="1"><label>Lenses</label></b-col>
            <b-col sm="8">
              <div class="ml-2">
                {{ lensAddonCombination.lenses.name }}
                <b-button class="ml-3" size="sm" @click="handleEditSelectedLenses">Edit</b-button>
              </div>
            </b-col>
          </b-row>
          <b-row class="mb-3" v-if="selectedLensModDisplay">
            <b-col class="d-flex align-items-center" sm="1"><label>Lens Style</label></b-col>
            <b-col sm="8">
              <div class="ml-2">{{ selectedLensModDisplay }}</div>
            </b-col>
          </b-row>
          <b-row class="mb-3" v-if="selectedLensModCorridorDisplay">
            <b-col class="d-flex align-items-center" sm="1"><label>Corridor</label></b-col>
            <b-col sm="8">
              <div class="ml-2">{{ selectedLensModCorridorDisplay }}</div>
            </b-col>
          </b-row>
          <b-row class="mb-3">
            <b-col sm="1"><label>Supplier</label></b-col>
            <b-col sm="11">
              <div class="ml-2">{{ orderLensData.supplier.name }}</div>
            </b-col>
          </b-row>
          <b-row class="mb-3">
            <b-col sm="1"><label>Product</label></b-col>
            <b-col sm="11">
              <div class="ml-2">{{ _.get(orderLensData, 'item.region_product.product.name') }}</div>
            </b-col>
          </b-row>
          <b-row class="mb-3">
            <b-col sm="2"
              ><label>Index</label>
              <div>{{ lensAddonCombination.lenses.index }}</div>
            </b-col>
            <b-col sm="2"
              ><label>Type</label>
              <div>{{ _.get(lensAddonCombination, 'lenses.lens_type.name') }}</div>
            </b-col>
            <b-col sm="2"
              ><label>Base Coating</label>
              <div>{{ _.get(lensAddonCombination, 'lenses.coating') }}</div>
            </b-col>
            <b-col sm="2"
              ><label>Treatment</label>
              <div>
                {{ _.get(lensAddonCombination, 'lenses.treatment') }}
              </div>
            </b-col>
            <b-col sm="2"
              ><label>+ Tint</label>
              <div v-if="lensAddonCombination.tint">{{ lensAddonCombination.tint.name }}</div>
            </b-col>
            <b-col sm="2"
              ><label>+ Coating</label>
              <div v-if="lensAddonCombination.coating">{{ lensAddonCombination.coating.name }}</div>
            </b-col>
          </b-row>
          <hr class="my-4" />
          <b-row>
            <b-col sm="3"
              ><label>Prescription</label>
              <div>
                <PrescriptionDetail
                  :prescription="prescriptionDetail"
                  :lensesToPick="lensesToPick"
                ></PrescriptionDetail>
              </div>
            </b-col>
            <b-col sm="3" v-if="additionalInfo.pdPresenter"
              ><label>Width</label>
              <div>
                <PupilDistanceDetail
                  :pd="additionalInfo.pdPresenter"
                  :sourceMessage="sourceMessage"
                  type="width"
                ></PupilDistanceDetail>
              </div>
            </b-col>
            <b-col sm="3" v-if="additionalInfo.pdPresenter && hasPdHeight"
              ><label>Height</label>
              <div>
                <PupilDistanceDetail
                  :pd="additionalInfo.pdPresenter"
                  :sourceMessage="sourceMessage"
                  type="height"
                ></PupilDistanceDetail>
              </div>
            </b-col>
            <b-col sm="3">
              <label>Frame Size</label>
              <div class="mb-1">
                <template v-if="frameSizeId">{{ frameSizeId }}</template>
                <template v-else>&mdash;</template>
              </div>

              <label>Frame Color</label>
              <div class="mb-1">
                <template v-if="frameColor">{{ frameColor }}</template>
                <template v-else>&mdash;</template>
              </div>
            </b-col>
          </b-row>
          <hr class="my-4" />

          <b-row>
            <b-col :cols="checkSuplierForEdge ? '6' : '12'">
              <b-form-group class="flex-fill" label="Type" label-for="type">
                <b-form-radio-group
                  class="custom-radio-button"
                  id="type"
                  v-model="form.lenses"
                  :options="lensesOptions"
                  buttons=""
                  button-variant="white"
                ></b-form-radio-group>
              </b-form-group>
            </b-col>

            <b-col cols="6" v-if="checkSuplierForEdge">
              <b-form-group
                class="flex-fill"
                label="Edging"
                label-for="edge"
                v-if="!isLoadingSupplier"
              >
                <b-form-select
                  id="edge"
                  v-model="form.need_to_edge"
                  :options="edgeOptions"
                  name="edge"
                />
              </b-form-group>
              <div
                v-else
                style="display: flex; justify-content: center; align-items: center; height: 100%"
              >
                <b-spinner variant="primary" label="Loading..." />
              </div>
            </b-col>
            <b-col cols="6">
              <b-form-group label="From" label-for="sender"
                ><select class="form-control" disabled="">
                  <option>{{ fromLocationText }}</option>
                </select></b-form-group
              >
            </b-col>
            <b-col cols="6">
              <ValidationProvider name="Deliver to" rules="required" v-slot="validationContext">
                <b-form-group label="Deliver to" label-for="to">
                  <b-form-select
                    id="delivery"
                    name="to"
                    v-model="form.to"
                    :state="getValidationState(validationContext)"
                    :options="deliveryOptions"
                    aria-describedby="to-feedback"
                    ><template v-slot:first="">
                      <b-form-select-option :value="null" disabled=""
                        >Please select</b-form-select-option
                      >
                    </template></b-form-select
                  >
                  <b-form-invalid-feedback id="to-feedback">{{
                    validationContext.errors[0]
                  }}</b-form-invalid-feedback>
                </b-form-group>
              </ValidationProvider>
            </b-col>
          </b-row>
          <b-form-group label="Contact (required)" label-for="contact">
            <ValidationProvider name="Contact" rules="required" v-slot="validationContext">
              <b-form-select
                id="contact"
                name="contact"
                v-model="form.contact"
                :state="getValidationState(validationContext)"
                :options="getContacts"
                value-field="id"
                text-field="mail"
                aria-describedby="contact-feedback"
                ><template v-slot:first="">
                  <b-form-select-option :value="null" disabled=""
                    >Please select</b-form-select-option
                  >
                </template></b-form-select
              >
              <b-form-invalid-feedback id="contact-feedback">{{
                validationContext.errors[0]
              }}</b-form-invalid-feedback>
            </ValidationProvider>
          </b-form-group>
          <p class="warning" v-if="checkWarningMessage">
            {{ checkWarningMessage }}
          </p>
          <b-form-group label="Notes" label-for="notes">
            <ValidationProvider
              name="notes"
              :rules="getNotesValidationRules()"
              v-slot="validationContext"
            >
              <b-form-textarea
                id="notes"
                name="notes"
                :state="getValidationState(validationContext)"
                v-model="form.notes"
                rows="3"
                placeholder="Additional requirements or details..."
              ></b-form-textarea>
              <b-form-invalid-feedback id="notes">{{
                validationContext.errors[0]
              }}</b-form-invalid-feedback>
            </ValidationProvider>
          </b-form-group>
          <div class="text-right" v-if="!isOnlyFilePrescription">
            <p class="text-danger mb-2" v-if="checkSuplierForEdge && !hasEdgingOptions">
              No edging options are available
            </p>
            <b-button
              class="button-with-loader"
              type="submit"
              variant="primary"
              :disabled="isSent || (checkSuplierForEdge && !hasEdgingOptions)"
              v-wait:disabled="'Comment Form'"
            >
              <v-wait for="Send Order">
                <template slot="waiting">
                  <div>
                    <b-spinner small=""></b-spinner>
                  </div> </template
                >Send</v-wait
              >
            </b-button>
          </div>
          <div v-else>
            <p class="error">Please edit prescription</p>
          </div>
        </b-form>
      </ValidationObserver>
    </div>
  </b-modal>
</template>

<script>
import { mapState } from 'vuex';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import apiOrders from '@/api/orders';
import PrescriptionDetail from './PrescriptionDetail.vue';
import PupilDistanceDetail from './PupilDistanceDetail.vue';

export default {
  name: 'OrderLensModal',
  components: {
    PrescriptionDetail,
    PupilDistanceDetail,
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      isSent: false,
      isLoadingSupplier: false,
      form: {
        to: null,
        lenses: 'both',
        need_to_edge: false,
        contact: null,
        reason: null,
        description: '',
        notes: null,
      },
      lensesOptions: [
        { text: 'Both', value: 'both' },
        { text: 'Right', value: 'right' },
        { text: 'Left', value: 'left' },
      ],
      supplier: {},
      reasonOptions: [
        '30 Day Warranty Exchange',
        'Customer Order Lost in Post',
        'Dispenser Error (In Store)',
        'Dispenser Error (Service Centre)',
        'Lab Error (Service Centre)',
        'Non Tolerance',
        'Online PD Tool',
        'Optom Error',
        'Supplier - Received Broken Lenses',
        'Supplier - Got wrong Index',
        'Supplier - Got wrong Diameter',
        'Supplier - Got wrong Color',
        'Supplier - Peel Off or bad Coating',
        'Supplier - Got scratched lenses',
        'Supplier - Got wrong Power',
        'Supplier - Got wrong Prism',
        'Supplier - Got wrong Lenses',
        'Other',
      ],
      contactOptions: [],
    };
  },
  computed: {
    ...mapState({
      orderLensData: state => state.orderLensModal.orderLensData,
      frameSizeId: state => state.orderLensModal.frameSizeId,
      outlets: state => state.outlets,
      order: state => state.orderLensModal.order,
      isDresdenReading: state => state.orderLensModal.isDresdenReading,
      dresdenReadingData: state => state.orderLensModal.dresdenReadingData,
    }),
    ...mapState('auth', {
      user: state => state.user,
      selectedRegion: state => state.selectedRegionId,
      authenticatedOutletId: state => state.selectedOutletId,
    }),
    sourceMessage() {
      const sourceId = this._.get(this.item, 'additional_info.data.pd.detail.source_id');
      if (sourceId && this.$store.state.pdSources) {
        const selectedSource = this.$store.state.pdSources.find(
          source => source.slug === 'o-pm' && source.id === sourceId,
        );
        if (selectedSource) {
          return true;
        }
      }
      return false;
    },
    checkWarningMessage() {
      return this._.get(
        this.orderLensData,
        'item.additional_info.data.lens_addon_combination.lens_warning_message',
      );
    },
    getContacts() {
      const getOrderType = this.orderLensData.supplier.order_type;
      let { contacts } = this.orderLensData.supplier;
      if (
        this.orderLensData.supplier.name === 'Hoya'
      ) {
        if (getOrderType === 'all') {
          if (contacts.filter(contact => contact.id === 99).length === 0) {
            contacts = [
              {
                created_at: '2021-10-14 10:31:56',
                first_name: 'Hoya',
                full_name: 'Hoya Australia',
                last_name: 'Australia',
                id: 99,
                mail: 'EDI',
                phone: null,
                region_id: 7,
                supplier_id: 27,
              },
            ];
          }

          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.form.contact = 99;
        } else if (getOrderType === 'api') {
          contacts = [
            {
              created_at: '2021-10-14 10:31:56',
              first_name: 'Hoya',
              full_name: 'Hoya Australia',
              last_name: 'Australia',
              mail: 'EDI',
              phone: null,
              id: 99,
              region_id: 7,
              supplier_id: 27,
            },
          ];
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.form.contact = 99;
        }
      }
      if (
        this.orderLensData.frameSizeId !== 'Non-Dresden' &&
        this.orderLensData.supplier.name === 'Shamir'
      ) {
        if (getOrderType === 'all') {
          if (contacts.filter(contact => contact.id === 99).length === 0) {
            contacts = [
              {
                created_at: '2021-10-14 10:31:56',
                first_name: 'SHAMIR',
                full_name: 'SHAMIR',
                last_name: 'SHAMIR',
                id: 999,
                mail: 'Shamir API',
                phone: null,
                region_id: 7,
                supplier_id: 31,
              },
            ];
          }
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          // this.form.need_to_edge = 'Cut';
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.form.contact = 999;
        } else if (getOrderType === 'api') {
          contacts = [
            {
              created_at: '2021-10-14 10:31:56',
              first_name: 'SHAMIR',
              full_name: 'SHAMIR',
              last_name: 'SHAMIR',
              id: 999,
              mail: 'Shamir API',
              phone: null,
              region_id: 7,
              supplier_id: 31,
            },
          ];
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.form.contact = 999;
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          // this.form.need_to_edge = 'Cut';
        }
      }

      if (
        this.orderLensData.frameSizeId !== 'Non-Dresden' &&
        this.orderLensData.supplier.name === 'PlasticPlus'
      ) {
        if (getOrderType === 'all') {
          if (contacts.filter(contact => contact.id === 99).length === 0) {
            contacts = [
              {
                created_at: '2021-10-14 10:31:56',
                first_name: 'PlasticPlus',
                full_name: 'PlasticPlus',
                last_name: 'PlasticPlus',
                id: 999,
                mail: 'PlasticPlus API',
                phone: null,
                region_id: 7,
                supplier_id: 34,
              },
            ];
          }
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          // this.form.need_to_edge = 'Cut';
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.form.contact = 999;
        } else if (getOrderType === 'api') {
          contacts = [
            {
              created_at: '2021-10-14 10:31:56',
              first_name: 'PlasticPlus',
              full_name: 'PlasticPlus',
              last_name: 'PlasticPlus',
              id: 999,
              mail: 'PlasticPlus API',
              phone: null,
              region_id: 7,
              supplier_id: 34,
            },
          ];
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.form.contact = 999;
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          // this.form.need_to_edge = 'Cut';
        }
      }

      if (this.form.need_to_edge === 'FTC') {
        contacts = contacts.filter(contact => contact.id !== 99);
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.form.contact = contacts[0].id;
      }

      const zeisSuplierIds = [2, 3, 4];
      contacts = contacts.filter(contact => !zeisSuplierIds.includes(contact.supplier_id));
      return contacts;
    },
    edgeOptions() {
      if (this.isLoadingSupplier) {
        return [{ text: 'Loading...', value: null, disabled: true }];
      }

      const cutOptions = this.orderLensData.supplier.cut_options;

      if (this.form.contact === 99 || this.form.contact === 999) {
        const options = [];
        if (cutOptions.UnCut.valid) {
          options.push({ text: 'Uncut lenses', value: 'UnCut' });
        }
        if (cutOptions.cut.valid) {
          options.push({
            text: `Cut to trace - ${this.frameSizeId?.toUpperCase?.() || ''}`,
            value: 'Cut',
          });
        }
        return options;
      }

      return [
        { text: 'Uncut lenses', value: 'UnCut' },
        {
          text: `Cut to trace - ${this.frameSizeId?.toUpperCase?.() || ''}`,
          value: 'Cut',
        },
        { text: 'Frame to follow', value: 'FTC' },
      ];
    },
    selectedOutletId() {
      const itemOutletId = this.item?.outlet?.id;
      if (itemOutletId) return itemOutletId;
      return this.authenticatedOutletId;
    },
    sender() {
      return `Dresden ${this.user.outlet.name}`;
    },
    frameColor() {
      return this._.get(this.additionalInfoData, 'customize.component.Frame.color.name');
    },
    item() {
      return this.orderLensData.item;
    },
    additionalInfo() {
      if (this.isDresdenReading && this.dresdenReadingData) {
        return {
          pdPresenter: {
            data: {
              width: {
                right: this._.get(this.dresdenReadingData, 'pd.right_monocular_dist_pd'),
                left: this._.get(this.dresdenReadingData, 'pd.left_monocular_dist_pd'),
                binocular: this._.get(this.dresdenReadingData, 'pd.binocular_dist_pd'),
              },
              height: {
                right: this._.get(this.dresdenReadingData, 'pd.right_height_multifocal'),
                left: this._.get(this.dresdenReadingData, 'pd.left_height_multifocal'),
              },
            },
          },
        };
      }
      return this._.get(this.item, 'additional_info');
    },
    fromLocationText() {
      if (
        this.orderLensData.supplier.name === 'Shamir' ||
        this.orderLensData.supplier.name === 'PlasticPlus'
      ) {
        return this.order.origin_outlet.name;
      }
      return `Dresden ${this.item?.outlet?.name}`;
    },
    additionalInfoData() {
      return this._.get(this.additionalInfo, 'data');
    },
    selectedLensModDisplay() {
      let mod = this._.get(this.additionalInfoData, 'selected_lens_mod');
      if (!mod) {
        const mods = this._.get(this.lensAddonCombination, 'lenses.lens_mods', [])
          .filter(m => m.type === 'lens_style');
        mod = mods.find(m => m.is_default) || mods[0];
      }
      if (!mod) return null;
      return `${mod.name} (${mod.code})`;
    },
    selectedLensModCorridorDisplay() {
      let mod = this._.get(this.additionalInfoData, 'selected_lens_mod_corridor');
      if (!mod) {
        const mods = this._.get(this.lensAddonCombination, 'lenses.lens_mods', [])
          .filter(m => m.type === 'lens_corridor');
        mod = mods.find(m => m.is_default) || mods[0];
      }
      if (!mod) return null;
      return `${mod.name} (${mod.code})`;
    },
    lensAddonCombination() {
      if (this.isDresdenReading && this.dresdenReadingData) {
        return this.dresdenReadingData.lens;
      }
      return this._.get(this.additionalInfoData, 'lens_addon_combination');
    },
    checkSuplierForEdge() {
      return (
        this.orderLensData.supplier.abbreviation === 'AU-HY' ||
        this.orderLensData.supplier.abbreviation === 'AU-SH' ||
        this.orderLensData.supplier.abbreviation === 'CA-PP' ||
        this.orderLensData.supplier.abbreviation === 'AU-PP'
      );
    },
    prescriptionDetail() {
      if (this.isDresdenReading && this.dresdenReadingData) {
        return this.dresdenReadingData.prescription;
      }
      return this._.get(this.additionalInfoData, 'prescription.detail');
    },
    lensesToPick() {
      if (this.isDresdenReading && this.dresdenReadingData) {
        const prescription = this.dresdenReadingData.prescription;
        const display = this._.get(prescription, 'standardised_prescription_display_array');
        if (display) {
          return {
            right: display.right,
            left: display.left,
            add: display.add || ''
          };
        }
      }
      return this._.get(this.additionalInfo, 'lensesToPick');
    },
    hasPdHeight() {
      return !!this._.get(this.additionalInfo, 'pdPresenter.data.height.right');
    },
    hasEdgingOptions() {
      if (!this.checkSuplierForEdge) return true;
      if (this.isLoadingSupplier) return false;
      
      const cutOptions = this.orderLensData.supplier.cut_options;
      return Object.values(cutOptions).some(option => option.valid);
    },
    standardDisplay() {
      return this._.get(this.prescriptionDetail, 'standardised_prescription_display_array');
    },
    isOnlyFilePrescription() {
      if (
        this.standardDisplay.left === 'L Plano DS' &&
        this.standardDisplay.right === 'R Plano DS' &&
        this.prescriptionDetail.add_int == null &&
        this.prescriptionDetail.add_near == null &&
        this.prescriptionDetail.left_int_add == null &&
        this.prescriptionDetail.right_int_add == null &&
        this.prescriptionDetail.right_near_add == null &&
        this.prescriptionDetail.left_near_add == null &&
        this.prescriptionDetail.files &&
        this.prescriptionDetail.files.length > 0
      ) {
        return true;
      }
      return false;
    },
    frameShapeDisplay() {
      // Get frame shape from the item data
      const frameShape = this._.get(this.item, 'additional_info.data.customize.component.Frame.shape');
      const sizeId = this._.get(this.item, 'additional_info.data.customize.component.Frame.sizeId');
      
      // Map shape values to display names
      const shapeMapping = {
        'classic': 'Original',
        'original': 'Original',
        'round': 'Capote',
        'capote': 'Capote',
        'non-dresden': 'Non-Dresden'
      };
      
      // If shape is explicitly set, use it
      if (frameShape) {
        return shapeMapping[frameShape.toLowerCase()] || frameShape;
      }
      
      // Fallback: determine shape from sizeId
      if (sizeId && sizeId.toLowerCase().includes('round')) {
        return 'Capote';
      }
      
      return 'Original'; // Default
    },
    frameSizeDisplay() {
      // Get frame size from the item data
      const frameSize = this._.get(this.item, 'additional_info.data.customize.component.Frame.sizeId');
      
      if (!frameSize || frameSize === 'Non-Dresden') {
        return null;
      }
      
      // Convert size to uppercase for display
      return frameSize.toUpperCase();
    },
    plasticPlusNotes() {
      // Generate notes for PlasticPlus orders
      if (this.orderLensData.supplier.name !== 'PlasticPlus') {
        return null;
      }
      
      const shape = this.frameShapeDisplay;
      const size = this.frameSizeDisplay;
      const frameShape = this._.get(this.item, 'additional_info.data.customize.component.Frame.shape');

      const shapeMapping = {
        'capote': 'Round',
        'original': 'Rectangle',
      };

      let notes = `${shape} (${shapeMapping[frameShape.toLowerCase()] || frameShape}),`;
      
      if (size) {
        notes += ` ${size}`;
      }

      let isStock = false;
      const checkStock = this._.get(this.item, 'additional_info.data.lens_addon_combination.lenses.name');
      if (checkStock.toLowerCase().includes('stock')) {
        isStock = true;
      }else{
        const lensManufacturing = this._.get(this.item, 'additional_info.data.lens_addon_combination.lenses.lens_type.lens_manufacturing');
        if (lensManufacturing.toLowerCase().includes('stock')) {
          isStock = true;
        }
      }

      if(isStock){
        notes += ', Stock';
      }
      
      return notes;
    },
    deliveryOptions() {
      if (
        this.orderLensData.supplier.name === 'Shamir' ||
        this.orderLensData.supplier.name === 'Hoya' ||
        this.orderLensData.supplier.name === 'PlasticPlus'
      ) {
        return this.outlets
          .filter(
            outlet =>
              (outlet.region_id === this.selectedRegion || outlet.id === this.selectedOutletId) &&
              outlet.is_active === true &&
              outlet.name !== 'Brunswick',
          )
          .map(outlet => {
            return {
              value: outlet.id,
              text: outlet.name,
              id: outlet.id,
            };
          });
      }

      return this.outlets
        .filter(
          outlet =>
            (outlet.region_id === this.selectedRegion || outlet.id === this.selectedOutletId) &&
            outlet.is_active === true &&
            outlet.address,
        )
        .map(outlet => {
          const addressString = this.$options.filters.address(this._.get(outlet, 'address'));
          return {
            value: addressString,
            text: addressString,
            id: outlet.id,
          };
        });
    },
    modalTitle() {
      const invoice = this.orderLensData.invoiceNumber;
      const firstName = this._.get(this.orderLensData, 'customer.first_name');

      if (invoice && firstName) {
        return `${invoice} — ${firstName} ${this.orderLensData.customer.last_name}`;
      }

      if (invoice && !firstName) {
        return `${this.orderLensData.invoiceNumber} — Guest`;
      }
      return invoice;
    },
    currentLocationAddress() {
      const location = this._.find(this.deliveryOptions, { id: this.selectedOutletId });
      return location ? location.value : null;
    },
    isStorePickup() {
      const deliveryOptionId = this._.get(this.item, 'delivery_type_id');
      return deliveryOptionId === 1;
    },
  },
  methods: {
    getNotesValidationRules() {
      const supplierName = this.orderLensData.supplier.name;
      const regionCode = this._.get(this.user, 'outlet.region.country_code');
      const contact = this.form.contact;
      
      if (supplierName === 'Hoya' ) {
        return 'hoyaMaxLength:20';
      }

      // Shamir supplier validation
      if (supplierName === 'Shamir') {
        return 'shamirMaxLength:65536';
      }
      
      // PlasticPlus supplier validation
      if (supplierName === 'PlasticPlus') {
        return 'plasticPlusMaxLength:100';
      }
      
      // Default validation for other suppliers
      if (contact === 99) {
        return this.orderLensData.count > 0 ? 'maxLength:18' : 'maxLength:25';
      }
      
      return '';
    },
    onShow() {
      this.populateData();
      if (this.orderLensData.frameSizeId !== 'Non-Dresden') {
        this.form.need_to_edge = 'Cut';
      } else {
        this.form.need_to_edge = 'FTC';
      }

      if (this.checkSuplierForEdge) {
        const cutOptions = this.orderLensData.supplier.cut_options;
        if (cutOptions && !cutOptions.cut.valid) {
          this.form.need_to_edge = 'UnCut';
        }
      }
      
      // Prefill notes for PlasticPlus orders
      if (this.orderLensData.supplier.name === 'PlasticPlus') {
        this.$nextTick(() => {
          this.form.notes = this.plasticPlusNotes;
        });
      }
      
      // check shamir order
      if (this.orderLensData.supplier.name === 'Shamir') {
        const originOutletId = this._.get(this.order, 'origin_outlet.id');
        const originOutletName = this._.get(this.order, 'origin_outlet.name');
        if (originOutletId) {
          const mappingOutlet = {
            // eslint-disable-next-line prettier/prettier
            BHF: '25',
            'Ecommerce NZ': '25',
            'Service Centre NZ': '25',
            'Online NZ': '25',
            'Online AU': '25',
            'ONLINE AU DEV': '25',
          };
          if (this.isStorePickup) {
            this.form.to = this.selectedOutletId;
          } else if (originOutletName in mappingOutlet) {
            this.form.to = mappingOutlet[originOutletName];
          } else {
            const littleItallyOutletId = 9;
            let checkOutletExists = this.deliveryOptions.filter(
              outlet => outlet.id === littleItallyOutletId,
            );

            if (checkOutletExists.length > 0) {
              this.form.to = littleItallyOutletId;
            } else {
              checkOutletExists = this.deliveryOptions.filter(
                outlet => outlet.id === originOutletId,
              );
              if (checkOutletExists.length > 0) {
                this.form.to = originOutletId;
              }
            }
          }
        }
      }
    },

    onHide() {
      this.$store.commit('addressModal/HIDE_NEW_ADDRESS_FORM');
    },
    populateData() {
      this.form.lenses = 'both';
      this.form.notes = null;
      const { defaultContact } = this.orderLensData;
      const contact = defaultContact
        ? this.orderLensData.supplier.contacts.find(sc => sc.id === defaultContact.id)
        : null;
      this.form.contact = contact ? contact.id : null;
      this.form.to = this.currentLocationAddress ? this.currentLocationAddress : null;
      this.form.reason = null;
      this.form.description = '';
      this.isSent = false;
    },
    sendOrder() {
      this.isSent = true;
      let contactId = this.form.contact;
      let orderType = null;
      console.log(this.form.contact);
      if (this.form.contact === 99) {
        orderType = 'api';
        contactId = 8;
      }
      if (this.form.contact === 999) {
        orderType = 'api';
        if (this.orderLensData.supplier.name === 'PlasticPlus') {
          contactId = 34;
        } else {
          contactId = 31;
        }
      }
      this.$wait.start('Send Order');
      const mailData = {
        type: 'customer',
        outlet_id: this.orderLensData.order.origin_outlet.id,
        user_id: this.user.id,
        order_id: this.item.order_id,
        product_id: this.item.region_product.product_id,
        item: this.item,
        frame_size: this.frameSizeId,
        to: this.form.to,
        lenses: this.form.lenses,
        from: this.fromLocationText,
        supplier_contact_id: contactId,
        order_type: orderType,
        notes: this.form.notes,
        reason: this.form.reason,
        description: this.form.description,
        need_to_edge: this.form.need_to_edge,
        is_dresden_reading: this.isDresdenReading || false,
        dresden_reading_data: this.isDresdenReading ? this.dresdenReadingData : null,
      };
      apiOrders
        .orderLens(mailData)
        .then(() => {
          this.$store.dispatch('showSuccessAlert', 'Lens ordered successfully.');
          apiOrders.getOrder(this.item.order_id).then(({ data: { data: job } }) => {
            this.$store.commit('UPDATE_JOB_IN_JOBS', job);
          });
        })
        .catch(({ response }) => {
          this.$store.dispatch('showErrorAlert', this._.get(response, 'data.error', 'Error'));
        })
        .finally(() => {
          this.$wait.end('Send Order');
          this.$bvModal.hide('order-lens-modal');
          this.form.contact = null;
        });
    },
    handleEditSelectedLenses() {
      this.orderLensData.vm.openEditJobModal(4);
      this.$bvModal.hide('order-lens-modal');
    },
  },
  watch: {
    // eslint-disable-next-line func-names
    'form.contact': function (newContact) {
      let oldLensesOptions = [
        { text: 'Both', value: 'both' },
        { text: 'Right', value: 'right' },
        { text: 'Left', value: 'left' },
      ];
      if (newContact === 999 && this.orderLensData.supplier.name !== 'PlasticPlus') {
        oldLensesOptions = [{ text: 'Both', value: 'both' }];
      }
      this.lensesOptions = oldLensesOptions;
    },
    // eslint-disable-next-line func-names
    'form.lenses': async function (newValue) {
      this.isLoadingSupplier = true;
      try {
        const {
          data: { data: supplier },
        } = await apiOrders.getSupplier(
          this.orderLensData.supplier.id,
          this._.get(this.item, 'id'),
          newValue,
        );

        // Use Vue.$set to ensure reactivity
        this.$set(this.orderLensData, 'supplier', supplier);

        // Reset edge option if it's not valid with new supplier data
        if (!supplier.cut_options[this.form.need_to_edge]?.valid) {
          this.form.need_to_edge =
            Object.keys(supplier.cut_options).find(key => supplier.cut_options[key].valid) || false;
        }
      } catch (error) {
        this.$store.dispatch(
          'showErrorAlert',
          this._.get(error, 'response.data.error', 'Error updating supplier data'),
        );
      } finally {
        this.isLoadingSupplier = false;
      }
    },
  },
};
</script>
